import { Component, OnInit, ElementRef, Input, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import * as vis from 'vis';
import * as _ from 'lodash';
import moment from 'moment';

import { ScreensStateService } from '../../core/services/screen-state/screens-state.service';
import { MonitorMenuService } from '../../core/services/dialog-services/monitor-menu.service';
import { SchedulesService } from '../../core/services/http-services/schedules.service';
import { MonitorFilterPipe } from './../../core/pipes/monitor-filter.pipe';
import { PlaylistsService } from '../../core/services/http-services/playlists.service';

import { Monitor } from './../../core/models/entities/monitor.type';
import { Schedule } from './../../core/models/entities/schedule.type';
import { ScheduleStatus } from '../../core/enums/schedule-status.enum';
import { TimelineInterface } from './../../core/interfaces/timeline.interface';
import { ScheduleSettingsMode } from './../../core/enums/screen-settings-mode.enum';
import { BehaviorScreen } from '../../core/models/view-models/monitor-models/screen.type';
import { MonitorKey } from '../../core/models/view-models/monitor-models/monitor-key.type';
import { TimelineRequestItem } from './../../core/models/view-models/schedule-models/timeline-request-result.type';
import { TimelineRequestResult } from '../../core/models/view-models/schedule-models/timeline-request-result.type';
import { TimelineRequestParameters } from '../../core/models/view-models/schedule-models/timeline-request-parameters.type';
import { TimelineClassesEnum } from './../../core/enums/timeline-classes.enum';
import { Machine } from '../../core/models/entities/machine.type';
import { AppConstants } from './../../app.constants';
import { PlaylistStateService } from '../../core/services/screen-state/playlists-state.service';
import { ScheduleType } from '../../core/models/enums/schedule-type.enum';
import { CalendarViewMode } from '../../core/enums/calendar-view-mode.enum';

@Component({
  selector: 'timeline',
  template: '',
  styleUrls: ['./timeline.component.scss'],
})
export class TimelineComponent implements OnInit, OnChanges, OnDestroy {
  @Input() date: Date;
  @Input() locations: Machine[] = [];
  @Input() monitorFilter: string;
  @Input() screens: BehaviorScreen[] = [];
  @Input() calendarMode: CalendarViewMode;

  private timelineData: TimelineRequestResult = null;
  private timeline: TimelineInterface;
  private subscriptions = new Subscription();

  private readonly timelineStartTime = ' 00:00:00';
  private readonly timelineEndTime = ' 23:59:59';
  private readonly defaultTimelineStep = 1;
  private readonly defaultTimelineScale = 'hour';

  constructor(
    private readonly element: ElementRef,
    private readonly scheduleService: SchedulesService,
    private readonly screenStateService: ScreensStateService,
    private readonly monitorMenuService: MonitorMenuService,
    private readonly monitorFilterPipe: MonitorFilterPipe,
    private readonly playlistDataService: PlaylistsService,
    public playlistStateService: PlaylistStateService
  ) {
    this.subscriptions.add(
      this.screenStateService.someSchedulesUpdated.subscribe(() =>
        this.refreshTimeline()
      )
    );

    this.subscriptions.add(
      this.playlistDataService.playlistCollectionChanged.subscribe(() =>
        this.refreshTimeline()
      )
    );
  }

  ngOnInit(): void {
    const options = this.makeOptions();
    this.timeline = new vis.Timeline(this.element.nativeElement, [], options);
    this.defineTimelineEvents();
    this.refreshTimeline();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.date || changes.screens || changes.calendarMode) {
      this.refreshTimeline();
    }

    if (changes.screens || changes.monitorFilter) {
      this.timelineRequestItemsFilter();
    }

    if (changes.screens && this.screens?.length) {
      const screensToDisconnect = this.screenStateService.filterOutSelectedScreens(this.screens);
      this.screenStateService.disconnectScreens(screensToDisconnect);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private timelineRequestItemsFilter(): void {

    if (!this.timelineData) {
      return;
    }

    const selectedLocation = this.locations.find((location) => location.IsSelected);
    const monitors = this.mapTimelineItemsToMonitors();
    let filteredMonitors = this.monitorFilterPipe.transform(
      monitors,
      selectedLocation,
      this.monitorFilter
    );

    if (selectedLocation.PhysicalAddress) {
      filteredMonitors = filteredMonitors.filter(
        (monitor) =>
          monitor.MachinePhysicalAddress === selectedLocation.PhysicalAddress
      );
    }

    const filteredMonitorsIds = filteredMonitors.map((monitor) => monitor.MonitorHandler);
    const items = this.timelineData.TimelineRequestItems.filter((item) =>
      filteredMonitorsIds.includes(item.Monitor.MonitorHandler)
    );

    this.redrawTimeline(items);
  }

  private mapTimelineItemsToMonitors(): Monitor[] {
    return this.timelineData.TimelineRequestItems.map((item) => {
      const machineRelatedToLocation = this.locations.find(
        (location) =>
          location.PhysicalAddress === item.Monitor.MachinePhysicalAddress
      );

      return {
        ...item.Monitor,
        Machine: {
          ...item.Monitor.Machine,
          ShowPrimaryScreen: machineRelatedToLocation?.ShowPrimaryScreen,
        },
      };
    });
  }

  private refreshTimeline(): void {
    if (!this.screens?.length || !this.timeline) {
      return;
    }

    const monitorKeys: MonitorKey[] = _.map(
      this.screens,
      (screen: BehaviorScreen) =>
        new MonitorKey(screen.MonitorHandler, screen.MachinePhysicalAddress)
    );
    const timelineRequestParams = new TimelineRequestParameters(
      monitorKeys,
      this.date
    );

    this.subscriptions.add(
      this.scheduleService
        .getSchedulesByTimelineRequest(timelineRequestParams)
        .subscribe((data) => {
          this.timelineData = data;

          const { start, end } = this.createTimeString();

          this.timeline.setOptions(this.makeOptions());
          this.timelineRequestItemsFilter();
          this.timeline.setWindow(start, end);
          setTimeout(() => this.updateTimelineZoom(), 300);
        })
    );
  }

  private updateTimelineZoom(): void {
    if (
      this.calendarMode === CalendarViewMode.Day &&
      (this.timeline.timeAxis.step.step !== this.defaultTimelineStep ||
      this.timeline.timeAxis.step.scale !== this.defaultTimelineScale)
    ) {
      this.timeline.zoomIn(this.defaultTimelineStep, null, () => {
        if (moment(this.date).isSame(new Date(), 'day')) {
          this.timeline.moveTo(new Date());
        }
      });
    }
  }

  private createTimeString(): { start: string; end: string } {
    let start: string;
    let end: string;

    switch(this.calendarMode) {
      case CalendarViewMode.Day:
        start = this.date.toDateString() + this.timelineStartTime;
        end = this.date.toDateString() + this.timelineEndTime;
        break;
      case CalendarViewMode.Week:
        start = moment(this.date).toDate().toDateString() + this.timelineStartTime;
        end = moment(this.date).add(1, 'week').toDate().toDateString() + this.timelineEndTime;
        break
      case CalendarViewMode.Month:
        start = moment(this.date).toDate().toDateString() + this.timelineStartTime;
        end = moment(this.date).add(1, 'month').toDate().toDateString() + this.timelineEndTime;
        break;
    }

    return { start, end };
  }

  private makeOptions(): vis.TimelineOptions {
    const { start, end } = this.createTimeString();

    return {
      orientation: { axis: 'top', item: 'top' },
      min: start,
      max: end,
      selectable: true,
      format: {
        minorLabels: {
          hour: 'HH:mm',
        },
      },
    };
  }

  private redrawTimeline(timelineItems: TimelineRequestItem[]): void {
    const items = new vis.DataSet<{ [key: string]: any }>();
    const groups = new vis.DataSet<{ [key: string]: any }>();

    timelineItems.forEach((monData) => {
      // create group
      const idGroup = `${monData.Monitor.MonitorHandler}:${monData.Monitor.MachinePhysicalAddress}`;
      groups.add({
        id: idGroup,
        content: `<mat-radio-button value=[${monData.Monitor.MonitorTitle}]>${monData.Monitor.MonitorTitle}</mat-radio-button>`,
        data: monData.Monitor,
      });

      for (let i = 0; i < this.getDaysRange(); i++) {
        monData.ScheduleItems.forEach((schData) => {
          const processingDate = moment(this.date).add(i, 'day').toDate().toDateString();
          let start: string;
          let end: string;
          let className: string;

          if (schData.AllDay) {
            start = processingDate + this.timelineStartTime;
            end = processingDate + this.timelineEndTime;
          } else {
            if (schData.ScheduleType === ScheduleType.ByDate) {
              start = processingDate + ' ' +
                (moment(new Date(schData.StartDate)).add(1, 'day').toDate() < moment(this.date).add(i, 'day').toDate()
                  ? this.timelineStartTime
                  : schData.StartTime);
              end = processingDate + ' ' +
                (new Date(schData.EndDate) > moment(this.date).add(i, 'day').toDate() ? this.timelineEndTime : schData.EndTime);
            } else {
              start = processingDate + ' ' + schData.StartTime;
              end = processingDate + ' ' + schData.EndTime;
            }
          }

          switch (schData.ScheduleStatus) {
            case ScheduleStatus.Interrupted:
              className = TimelineClassesEnum.TimelineInterruptedClass;
              break;
            case ScheduleStatus.Started:
              className = TimelineClassesEnum.TimelineStartedClass;
              break;
            case ScheduleStatus.Stopped:
              className = TimelineClassesEnum.TimelineStoppedClass;
              break;
            default:
              className = TimelineClassesEnum.TimelineDefaultClass;
              break;
          }

          if (this.isItemDateRelated(schData, i)) {
            items.add({
              id: `${schData.Id}:${i}`,
              group: idGroup,
              subgroup: schData.Id,
              content: `${AppConstants.TIMELINE_PLAYLIST_ICON_PATH} ${schData.Playlist.PlaylistTitle}`,
              start: start,
              end: end,
              className: className,
              data: schData
            });
          }
        });
      }
    });

    this.timeline.setGroups(groups);
    this.timeline.setItems(items);
  }

  private isItemDateRelated(schedule: Schedule, days: number): boolean {
    const dayName = moment(this.date).add(days, 'days').format('dddd');

    switch (schedule.ScheduleType) {
      case ScheduleSettingsMode.Daily:
        return true;
      case ScheduleSettingsMode.Weekly:
        return schedule[dayName];
      case ScheduleSettingsMode.Range:
        const cleanStartDate = +new Date(schedule.StartDate.split('T')[0]);
        const cleanEndDate = +new Date(schedule.EndDate.split('T')[0]);
        const cleanDateNow = moment(new Date(this.date.toISOString().split('T')[0])).add(days, 'days').valueOf();
        return cleanDateNow >= cleanStartDate && cleanDateNow <= cleanEndDate;
      default:
        return false;
    }
  }

  private defineTimelineEvents(): void {
    this.timeline.on('click', (e) => {
      e.event.preventDefault();
      e.event.stopPropagation();
    });

    this.timeline.on('itemover', ({ item, event }: { item: string; event: MouseEvent }) => {
      const scheduleId = Number(item?.split(':')[0]);

      event.preventDefault(); // to prevent the browser contextmenu
      event.stopPropagation();

      if (scheduleId && this.calendarMode === CalendarViewMode.Day) {
        const monItem = this.timelineData.TimelineRequestItems.find(
          (k) => k.ScheduleItems.find((s) => s.Id === scheduleId) != null
        );
        const schItem = monItem.ScheduleItems.find(
          (s) => s.Id === scheduleId
        );
        const screen = this.screens.find(
          (k) =>
            k.MachinePhysicalAddress ===
            monItem.Monitor.MachinePhysicalAddress &&
            k.MonitorHandler === monItem.Monitor.MonitorHandler
        );

        this.monitorMenuService.showTimelineMonitorMenu(
          screen,
          schItem,
          event.x,
          event.y
        );
      }
    });
  }

  private getDaysRange(): number {
    const { start, end } = this.createTimeString();
    return moment(new Date(end)).diff(moment(new Date(start)), 'days') + 1;
  }
}
